import axios from 'axios';
import { Buffer } from 'buffer';
import { xray } from 'lib/xray';
import { PDFConverterAPICall } from 'reactApp/api/PDFConverterAPICall';
import { PdfInfoAPICall } from 'reactApp/api/PdfInfoAPICall';
import { PdfViewPartAPICall } from 'reactApp/api/PdfViewPartAPICall';
import { getExtension } from 'reactApp/modules/file/utils';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { parse } from 'reactApp/utils/parseMultipartData';

type Content = Buffer | null;

export const isBuffer = (data: any): data is Buffer => !!data?.byteLength;

export const downloadDocument = async (url: string, isStock = false, radars?: { onError: () => void; onSuccess: () => void }) => {
    try {
        let content;

        if (isStock) {
            const { data: response } = await axios.get<Content>(url, {
                withCredentials: true,
                params: { no_redirect: true },
                responseType: 'arraybuffer',
            });

            content = response;
        } else {
            const { data: response } = await axios.get(url, { withCredentials: true, params: { no_redirect: true } });
            const { data: file } = await axios.get<Content>(response.redirect, { responseType: 'arraybuffer' });
            content = file;
        }

        radars?.onSuccess();

        return { content };
    } catch (error: any) {
        radars?.onError();

        return { error };
    }
};

export const downloadDocumentEmbedded = async (url: string, options?: { radars: { onError: () => void; onSuccess: () => void } }) => {
    try {
        const { data: content } = await axios.get<Content>(url, { responseType: 'arraybuffer' });
        options?.radars?.onSuccess();
        return { content };
    } catch (error: any) {
        options?.radars?.onError();
        return { error };
    }
};

export const convertToPdfAndDownload = async (
    url: string,
    { converterStorage, ext, itemStorage }: { converterStorage: EStorageType; ext?: string; itemStorage: EStorageType }
) => {
    const extension = ext || getExtension(url);

    xray.send(`ovidius_view_${itemStorage}_${extension}`);

    try {
        const { data: content } = await new PDFConverterAPICall().makeRequest({ [converterStorage]: url });

        xray.send(`ovidius_view-success_${itemStorage}_${extension}`);

        return { content };
    } catch (error: any) {
        xray.send(`ovidius_view-error_${itemStorage}_${extension}`);

        return { error };
    }
};

export const getPdfFileParts = async (url: string, storage: EStorageType) => {
    try {
        const { headers, data } = await new PdfInfoAPICall().makeRequest({
            [storage]: url,
        });

        const contentType = headers['content-type'];
        if (!contentType) {
            xray.send('ovidius_parts_no_content_type', {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    error: 'no_content_type',
                },
            });
            return {};
        }

        const boundary = contentType?.split('boundary=')?.[1];
        const buffer = Buffer.from(data);
        const parts = parse(buffer, boundary);

        if (parts.length !== 2) {
            xray.send('ovidius_parts_format_error', {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    error: 'parts_format_error',
                    parts: parts.length,
                },
            });
            return {};
        }

        const partsInfoData = parts[0]?.data;
        const partsString = new TextDecoder().decode(partsInfoData);
        const partsInfo = JSON.parse(partsString);

        const firstPart = parts[1]?.data;

        xray.send('ovidius_parts_success');

        return { partsInfo, firstPart };
    } catch (error: any) {
        xray.send('ovidius_get_parts_error', {
            rlog: 'cloud_norm_pdf_err',
            rlog_message: {
                message: 'get_parts_error',
                error,
            },
        });
        return {};
    }
};

export const getPdfFilePartCallback = (url: string, storage: EStorageType) => {
    return async (partId: string) => {
        try {
            const { data } = await new PdfViewPartAPICall().makeRequest({
                [storage]: url,
                part_id: partId,
            });

            xray.send('ovidius_get_part_success');

            return { content: Buffer.from(data) };
        } catch (error: any) {
            xray.send('ovidius_get_part_inf_error', {
                rlog: 'cloud_norm_pdf_err',
                rlog_message: {
                    message: 'get_part_inf_error',
                    error,
                    partId,
                },
            });
            return {};
        }
    };
};
